import { Theme } from "@ctypes";
import { palette } from "@layout/palette";
import { homeLight as t } from "./homeTokens";

/**
 * Light theme on the home-spec design system: slate ink on a warm paper
 * mesh. Same roles as the dark theme via the homeLight token set – the
 * filled button inverts to ink-on-paper, cards go translucent white.
 */
export const lightTheme: Theme = {
  meta: {
    dark: false,
  },
  background: t.ground,
  text: {
    default: t.ink,
    secondary: t.ink66,
    tertiary: t.ink44,
    error: t.error,
    highlight: t.copper,
  },
  disclaimer: {
    ssn: {
      background: t.sageFill,
    },
  },
  header: {
    background: t.navBg,
    item: {
      selected: t.fill10,
      text: t.ink,
    },
  },
  body: {
    background: "transparent",
  },
  transfers: {
    item: {
      background: t.cardBg,
    },
    option: {
      background: {
        selected: t.copper,
        default: t.cardBg,
      },
      text: {
        selected: t.ground,
        default: t.ink,
      },
    },
  },
  dashboard: {
    header: t.ink,
    beneficiary: {
      item: {
        background: {
          selected: t.copper,
          default: t.copperFill,
        },
        text: {
          default: t.ink,
        },
      },
    },
    deposit: {
      swirl: t.fill10,
    },
    beneficiarySsnBanner: {
      background: t.copperFill,
      icon: t.copper,
    },
  },
  accounts: {
    item: {
      goal: {
        background: t.copperFill,
        text: t.copperText,
      },
      amount: {
        current: t.ink,
        projected: t.ink44,
      },
      progress: {
        default: t.copper,
        background: t.fill10,
      },
      empty: {
        default: t.fill10,
      },
    },
  },
  form: {
    background: "transparent",
  },
  error: {
    background: t.errorStrong,
    disclaimer: {
      background: t.copperFill,
      info: t.copper,
    },
  },
  success: {
    background: t.sageDeep,
  },
  button: {
    background: {
      primary: t.ink,
      secondary: t.fill10,
      disabled: t.fill25,
      danger: t.errorStrong,
      inverse: t.ground,
    },
    text: {
      primary: t.ground,
      secondary: t.ink,
      inverse: t.ink,
    },
  },
  verification: {
    banner: {
      home: {
        inProgress: palette.skyblue[40],
        error: t.copperFill,
      },
      contributions: {
        inProgress: t.copperFill,
        error: t.copperFill,
      },
    },
  },
  chart: {
    projection: {
      default: {
        background: t.cardBg,
        returned: t.copper,
        invested: t.ink,
        text: t.ink,
        icon: t.ink,
        footer: t.fill06,
        adjuster: {
          background: "rgba(255,255,255,0.55)",
          fill: t.ink,
        },
      },
      inverted: {
        background: t.cardBg,
        invested: t.ink,
        returned: t.sage,
        text: t.ink,
        footer: t.fill06,
        switch: {
          icon: t.ink,
          selected: t.fill14,
          background: "rgba(255,255,255,0.55)",
        },
      },
    },
    performance: {
      background: t.cardBg,
      stroke: t.copper,
      footer: {
        selected: {
          text: t.ink,
          background: t.fill14,
        },
        disabled: {
          text: t.ink44,
        },
      },
    },
  },
  checkbox: {
    default: t.ink,
    variant: t.ink,
  },
  radio: {
    inner: t.copper,
    filled: t.fill14,
    unfilled: t.ink,
  },
  loader: {
    default: t.ink,
    secondary: t.ink66,
    inverse: t.ground,
  },
  goal: {
    icons: {
      trumpSidecar: palette.skyblue[100],
    },
    sidecar: {
      card: {
        background: palette.ebb[100],
      },
      negative: palette.mahogany[100],
      positive: palette.teal[100],
    },
    progress: {
      background: t.copperFill,
      bar: {
        background: t.sageChipBg,
        default: t.copper,
      },
    },
    deposits: {
      background: t.cardBg,
      text: t.ink,
    },
    share: {
      background: t.cardBg,
      text: {
        default: t.ink,
        secondary: t.sage,
      },
    },
    portfolio: {
      seemore: {
        background: "rgba(255,255,255,0.55)",
        text: t.ink,
      },
    },
    balanceSyncBanner: {
      background: palette.skyblue[30],
      icon: palette.skyblue[100],
    },
    harvest: {
      maximize: t.sageChipBg,
      taxFiling: {
        background: t.sageFill,
      },
    },
  },
  input: {
    background: t.fill07,
    text: t.ink,
    placeholder: t.ink38,
    error: t.error,
    selected: {
      background: t.ink,
      text: t.ground,
    },
  },
  investment: {
    downturn: {
      stroke: t.error,
      fill: {
        first: t.ink,
        second: t.error,
      },
      text: t.error,
      box: t.cardBg,
      cursor: {
        fill: t.cardSolid,
        stroke: t.error,
      },
      dash: t.ink,
      label: t.ink,
    },
    breakdown: {
      card: {
        background: t.cardBg,
        positive: t.sage,
        negative: t.error,
        neutral: t.ink44,
      },
    },
  },
  item: {
    background: "rgba(255,255,255,0.55)",
    highlight: t.ink66,
  },
  icon: {
    default: t.ink,
  },
  infoFill: {
    caution: {
      default: t.ink,
      background: t.sageDeep,
    },
  },
  infoBanner: {
    background: t.sageFill,
  },
  link: {
    default: t.copper,
    secondary: t.copperHover,
  },
  modal: {
    backdrop: palette.darkblack[100],
    background: t.cardSolid,
    item: "rgba(255,255,255,0.70)",
    button: {
      secondary: "rgba(255,255,255,0.70)",
    },
  },
  footer: {
    default: t.ground,
    secondary: "rgba(255,255,255,0.55)",
  },
  border: t.line12,
  exclusive: {
    default: t.copper,
    inverse: {
      text: t.sage,
      background: t.sageChipBg,
    },
  },
  relink: {
    recurring: {
      background: t.copperFill,
      icon: {
        background: t.ink,
        fill: t.errorStrong,
      },
    },
  },
  documents: {
    unread: t.copper,
  },
  validator: {
    valid: t.sage,
    invalid: t.error,
  },
  switch: {
    background: t.sageDeep,
  },
  slider: {
    background: t.ground,
  },
  progress: {
    default: "#E8E2D6",
  },
  portfolio: {
    background: t.sageFill,
  },
  card: {
    background: t.cardBg,
  },
  tab: {
    selected: t.ink,
    default: t.ink44,
  },
  auth: {
    icon: t.ink,
    background: t.cardBg,
    invest: {
      background: t.ground,
    },
  },
  step: {
    complete: t.sage,
    incomplete: t.ink44,
  },
  shared: {
    icon: {
      scheduled: {
        background: t.fill14,
      },
    },
    contribution: {
      default: t.sage,
    },
    badge: {
      default: palette.skyblue[100],
    },
    cta: {
      remove: t.copper,
      resend: t.copper,
      sent: t.sage,
      cancel: t.error,
    },
  },
  contact: {
    item1: t.sage,
    item2: t.copper,
    item3: palette.skyblue[100],
  },
  withdrawal: {
    success: {
      default: t.ink,
      background: t.ground,
    },
    availableFunds: {
      icon: t.sage,
      default: t.ink44,
    },
    listItem: {
      fill: t.copper,
      border: t.line12,
      background: "rgba(255,255,255,0.55)",
    },
  },
  promo: {
    invalid: {
      background: t.errorStrong,
      icon: t.ink,
      text: t.error,
    },
    valid: {
      default: t.sage,
      icon: t.ink,
    },
    allocate: {
      background: t.sageFill,
      button: {
        background: t.ink,
        text: t.ground,
      },
      pending: {
        background: t.ink,
        text: palette.rust[100],
      },
    },
  },
};
